// react
import { type ReactElement } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';

// components
// types / utils
import type { AnalysisType } from '@/features/energyxplorer/types';
import { formatNumber } from '@/utils/formatters';

const SECTION_CONFIG: Record<
  AnalysisType,
  { icon: ReactElement; unit: string }
> = {
  power: {
    icon: <BoltRoundedIcon fontSize="small" sx={{ color: 'power.main' }} />,
    unit: 'kWh',
  },
  water: {
    icon: (
      <WaterDropRoundedIcon fontSize="small" sx={{ color: 'water.main' }} />
    ),
    unit: 'm³',
  },
  heat: {
    icon: (
      <LocalFireDepartmentRoundedIcon
        fontSize="small"
        sx={{ color: 'heat.main' }}
      />
    ),
    unit: 'kWh',
  },
};

function getMeterName(meter: Record<string, unknown>): string {
  // Use || to fall through empty strings that analysis data may return
  return (
    (meter.display_name as string | null | undefined) ||
    (meter.DisplayName as string | null | undefined) ||
    (meter.Name as string | null | undefined) ||
    (meter.Address as string | null | undefined) ||
    String(meter.UnitId ?? meter.MeteringPointId ?? '')
  );
}

interface TypeAggregate {
  totalYear: number;
  yoyPct: number | null;
  co2Total: number;
}

function aggregateType(meters: Record<string, unknown>[]): TypeAggregate {
  const nonPv = meters.filter((m) => m.type_of_mp !== 'PV');
  let totalYear = 0;
  let prevYearTotal = 0;
  let hasPrev = false;
  let co2Total = 0;

  for (const m of nonPv) {
    const ty = (m.total_consumption_year as number | null) ?? 0;
    const yoy = m.yoy_ratio as number | null;
    totalYear += ty;
    co2Total += (m.total_co2e_year as number | null) ?? 0;
    if (yoy != null && yoy > 0) {
      prevYearTotal += ty / yoy;
      hasPrev = true;
    }
  }

  const yoyPct =
    hasPrev && prevYearTotal > 0 ? (totalYear / prevYearTotal - 1) * 100 : null;

  return { totalYear, yoyPct, co2Total };
}

function formatYoyPct(pct: number): string {
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function yoyColor(
  pct: number,
): 'error.main' | 'success.main' | 'text.secondary' {
  return pct > 2 ? 'error.main' : pct < -2 ? 'success.main' : 'text.secondary';
}

// ─── Summary card ─────────────────────────────────────────────────────────────

interface SummaryCardProps {
  icon: ReactElement;
  label: string;
  total: number;
  unit: string;
  yoyPct: number | null;
}

const SummaryCard = ({
  icon,
  label,
  total,
  unit,
  yoyPct,
}: SummaryCardProps) => (
  <Card sx={{ flex: 1, p: 1.5, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      {icon}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 0.5, lineHeight: 1 }}
        noWrap
      >
        {label}
      </Typography>
    </Box>
    <Typography variant="subtitle1" fontWeight={600} noWrap>
      {formatNumber(total)}
      {unit ? ` ${unit}` : ''}
    </Typography>
    {yoyPct != null && (
      <Typography variant="caption" color={yoyColor(yoyPct)}>
        {formatYoyPct(yoyPct)}
      </Typography>
    )}
  </Card>
);

// ─── Meter group (type header + individual rows) ───────────────────────────────

interface MeterGroupSectionProps {
  analysisType: AnalysisType;
  meters: Record<string, unknown>[];
  onNavigateToMeter: (
    meter: Record<string, unknown>,
    analysisType: AnalysisType,
  ) => void;
}

const MeterGroupSection = ({
  analysisType,
  meters,
  onNavigateToMeter,
}: MeterGroupSectionProps) => {
  const { t } = useTranslation(['energyxplorer']);
  const cfg = SECTION_CONFIG[analysisType];

  return (
    <Box>
      {/* Type aggregate header — not individually navigable */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'action.hover',
        }}
      >
        {cfg.icon}
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t(analysisType, { ns: 'glossary', defaultValue: analysisType })}
        </Typography>
      </Box>

      {meters.map((m, i) => {
        const name = getMeterName(m);
        const ty = m.total_consumption_year as number | null | undefined;
        const yoy = m.yoy_ratio as number | null | undefined;
        const mYoyPct = yoy != null ? (yoy - 1) * 100 : null;
        const isPV = m.type_of_mp === 'PV';

        return (
          <Box
            key={String(
              m.MeteringPointId ?? m.metering_point_id ?? m.UnitId ?? i,
            )}
            onClick={() => onNavigateToMeter(m, analysisType)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 3,
              pr: 1.5,
              py: 1,
              cursor: 'pointer',
              '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' },
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {isPV ? (
              <SolarPowerRoundedIcon
                fontSize="small"
                sx={{ color: 'power.main' }}
              />
            ) : (
              cfg.icon
            )}
            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
              {name}
            </Typography>
            {ty != null && (
              <Typography variant="body2">
                {formatNumber(ty)} {cfg.unit}
              </Typography>
            )}
            {mYoyPct != null && (
              <Typography
                variant="caption"
                color={yoyColor(mYoyPct)}
                sx={{ minWidth: 52, textAlign: 'right' }}
              >
                {formatYoyPct(mYoyPct)}
              </Typography>
            )}
            <ArrowForwardRoundedIcon
              fontSize="small"
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Root component ────────────────────────────────────────────────────────────

interface BuildingInfoTabProps {
  metersByType: {
    power: Record<string, unknown>[];
    water: Record<string, unknown>[];
    heat: Record<string, unknown>[];
  };
  onNavigateToMeter: (
    meter: Record<string, unknown>,
    analysisType: AnalysisType,
  ) => void;
  isLoading?: boolean;
}

const BuildingInfoTab = ({
  metersByType,
  onNavigateToMeter,
  isLoading,
}: BuildingInfoTabProps) => {
  const { t } = useTranslation(['energyxplorer']);

  const totalMeters =
    metersByType.power.length +
    metersByType.water.length +
    metersByType.heat.length;

  const powerAgg = aggregateType(metersByType.power);
  const heatAgg = aggregateType(metersByType.heat);
  const waterAgg = aggregateType(metersByType.water);
  const co2Total = powerAgg.co2Total + heatAgg.co2Total + waterAgg.co2Total;

  const activeTypes = (
    [
      { type: 'power' as AnalysisType, meters: metersByType.power },
      { type: 'heat' as AnalysisType, meters: metersByType.heat },
      { type: 'water' as AnalysisType, meters: metersByType.water },
    ] as const
  ).filter(({ meters }) => meters.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t('matrikel.consumption_title', {
          ns: 'energyxplorer',
          defaultValue: 'Forbrug og udvikling de sidste 12 måneder',
        })}
      </Typography>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : totalMeters === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 4 }}
        >
          {t('matrikel.no_meters_for_matrikel', { ns: 'energyxplorer' })}
        </Typography>
      ) : (
        <>
          {/* Summary stat cards */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {metersByType.power.length > 0 && (
              <SummaryCard
                icon={
                  <BoltRoundedIcon
                    fontSize="small"
                    sx={{ color: 'power.main' }}
                  />
                }
                label={t('power', {
                  ns: 'glossary',
                  defaultValue: 'Electricity',
                })}
                total={powerAgg.totalYear}
                unit="kWh"
                yoyPct={powerAgg.yoyPct}
              />
            )}
            {metersByType.heat.length > 0 && (
              <SummaryCard
                icon={
                  <LocalFireDepartmentRoundedIcon
                    fontSize="small"
                    sx={{ color: 'heat.main' }}
                  />
                }
                label={t('heat', { ns: 'glossary', defaultValue: 'Heat' })}
                total={heatAgg.totalYear}
                unit="kWh"
                yoyPct={heatAgg.yoyPct}
              />
            )}
            {metersByType.water.length > 0 && (
              <SummaryCard
                icon={
                  <WaterDropRoundedIcon
                    fontSize="small"
                    sx={{ color: 'water.main' }}
                  />
                }
                label={t('water', { ns: 'glossary', defaultValue: 'Water' })}
                total={waterAgg.totalYear}
                unit="m³"
                yoyPct={waterAgg.yoyPct}
              />
            )}
            {co2Total > 0 && (
              <SummaryCard
                icon={
                  <Co2RoundedIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                  />
                }
                label="CO₂"
                total={co2Total}
                unit=""
                yoyPct={null}
              />
            )}
          </Box>

          {/* Meters list grouped by type */}
          <Card sx={{ overflow: 'hidden' }}>
            <Typography
              variant="subtitle2"
              sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              {t('matrikel.meters', {
                ns: 'energyxplorer',
                defaultValue: 'Meters',
              })}
            </Typography>
            {activeTypes.map(({ type, meters }, idx) => (
              <Box key={type}>
                {idx > 0 && <Divider />}
                <MeterGroupSection
                  analysisType={type}
                  meters={meters}
                  onNavigateToMeter={onNavigateToMeter}
                />
              </Box>
            ))}
          </Card>
        </>
      )}
    </Box>
  );
};

export default BuildingInfoTab;
